/**
 * One stage of a workflow version, as a card (ADR-0009, FR-C3). Split out of
 * the editor in Wave 15F (C14; ADR-0033's *files over 500 lines are split
 * along their parts*). What each control does is unchanged — the rules below
 * that keep a stage's fields in agreement are the ones the editor always had.
 */

import { Button, Card, CardBody, CardHeader, Checkbox, Input, Select } from '@fiveox/ui'
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react'

import {
  ACTION_LABELS,
  CATEGORY_LABELS,
  type ActionKind,
  type Issue,
  type StageCategory,
  type StageDraft,
  type Vocabulary,
} from '../../lib/api/services'
import { IssueList } from './IssueList'

export interface StageCardProps {
  index: number
  stage: StageDraft
  /** Every stage, for the "go to" choices. */
  stages: StageDraft[]
  /** The version may be changed, and the caller may change it. */
  live: boolean
  moverRoles: Vocabulary['roles']
  actionKinds: Vocabulary['action_kinds']
  errors: Issue[]
  warnings: Issue[]
  onChange: (changes: Partial<StageDraft>) => void
  onMove: (to: number) => void
  onRemove: () => void
}

export function StageCard({
  index,
  stage,
  stages,
  live,
  moverRoles,
  actionKinds,
  errors,
  warnings,
  onChange: update,
  onMove,
  onRemove,
}: StageCardProps) {
  const others = stages.filter((_, i) => i !== index)
  /** The service's own refusal for one field, drawn under that field (C27,
   *  UAT report #13) as well as in the card's list. */
  const fieldError = (field: string) => errors.find((issue) => issue.field === field)?.message ?? null

  return (
    <Card data-stage={stage.key ?? index}>
      <CardHeader
        title={`${index + 1}. ${stage.name || 'Untitled stage'}`}
        description={CATEGORY_LABELS[stage.category]}
        action={
          live ? (
            <div className="flex gap-1">
              <Button variant="ghost" size="sm" onClick={() => onMove(index - 1)} aria-label="Move up">
                <ArrowUp size={16} aria-hidden />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => onMove(index + 1)} aria-label="Move down">
                <ArrowDown size={16} aria-hidden />
              </Button>
              <Button variant="ghost" size="sm" onClick={onRemove} aria-label="Remove">
                <Trash2 size={16} aria-hidden />
              </Button>
            </div>
          ) : undefined
        }
      />
      <CardBody className="space-y-5">
        <IssueList issues={errors} tone="error" />
        <IssueList issues={warnings} tone="warning" />

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Stage name"
            value={stage.name}
            disabled={!live}
            onChange={(e) => update({ name: e.target.value })}
          />
          <Select
            label="Category"
            value={stage.category}
            disabled={!live}
            onChange={(e) => {
              const category = e.target.value as StageCategory
              // The category and the review flag have to agree — a "Review"
              // stage that does not open a review is the one combination the
              // server refuses, so the form does not let a firm build it by hand.
              update({
                category,
                requires_review: category === 'review' ? true : stage.requires_review,
                client_visible: category === 'review' ? false : stage.client_visible,
                client_status: category === 'review' ? null : stage.client_status,
              })
            }}
          >
            {Object.entries(CATEGORY_LABELS).map(([code, label]) => (
              <option key={code} value={code}>
                {label}
              </option>
            ))}
          </Select>
        </div>

        <fieldset className="space-y-2">
          <legend className="text-foreground text-sm font-medium">Who may move a case into this stage</legend>
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            {moverRoles.map((role) => (
              <Checkbox
                key={role.code}
                label={role.name}
                checked={stage.allowed_roles.includes(role.code)}
                disabled={!live}
                onChange={(e) =>
                  update({
                    allowed_roles: e.target.checked
                      ? [...stage.allowed_roles, role.code]
                      : stage.allowed_roles.filter((r) => r !== role.code),
                  })
                }
              />
            ))}
          </div>
          <p className="text-muted-foreground text-xs">
            Only these four roles can move a case at all. An article assistant or a viewer cannot, so
            they are not offered here.
          </p>
        </fieldset>

        {!stage.requires_review && (
          <div className="space-y-3">
            <Checkbox
              label="The client can see this stage"
              checked={stage.client_visible}
              disabled={!live}
              onChange={(e) =>
                update({
                  client_visible: e.target.checked,
                  client_status: e.target.checked ? stage.client_status : null,
                })
              }
            />
            {stage.client_visible && (
              <Input
                label="What the client is told"
                value={stage.client_status ?? ''}
                disabled={!live}
                placeholder="With us"
                hint="The portal shows this and never your internal stage name."
                onChange={(e) => update({ client_status: e.target.value })}
              />
            )}
          </div>
        )}

        <div className="space-y-3">
          <Checkbox
            label="This stage holds the case for review"
            checked={stage.requires_review}
            disabled={!live || stage.category === 'review'}
            onChange={(e) =>
              update({
                requires_review: e.target.checked,
                client_visible: e.target.checked ? false : stage.client_visible,
                client_status: e.target.checked ? null : stage.client_status,
                on_approve: e.target.checked ? stage.on_approve : null,
                on_reject: e.target.checked ? stage.on_reject : null,
              })
            }
          />
          {stage.requires_review && (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                {(['on_approve', 'on_reject'] as const).map((field) => (
                  <Select
                    key={field}
                    label={field === 'on_approve' ? 'If approved, go to' : 'If rejected, go to'}
                    value={stage[field] ?? ''}
                    disabled={!live}
                    required
                    aria-required="true"
                    error={fieldError(field)}
                    onChange={(e) => update({ [field]: e.target.value || null })}
                  >
                    {/* Required on a review stage, so a placeholder and not a
                        choice (C27, UAT report #13). */}
                    <option value="" disabled hidden>
                      choose a stage
                    </option>
                    {others.map((other) => (
                      <option key={other.key} value={other.key ?? ''}>
                        {other.name}
                      </option>
                    ))}
                  </Select>
                ))}
              </div>
              <p className="text-muted-foreground text-xs">
                A review stage never appears to a client, and the case is frozen for everyone —
                including managers — until it is approved or rejected.
              </p>
            </>
          )}
        </div>

        {/* FR-F5, T-2.21 — the *client's* approval, which is a different
            decision from the reviewer's above: one is the firm signing off its
            own work, the other is the client signing off the firm's. Left empty
            on every stage unless a firm says otherwise. */}
        <div className="space-y-2">
          <Select
            label="When a client approves a document on this stage, go to"
            value={stage.advance_on_approval ?? ''}
            disabled={!live}
            onChange={(e) => update({ advance_on_approval: e.target.value || null })}
          >
            <option value="">stay where it is</option>
            {others.map((other) => (
              <option key={other.key} value={other.key ?? ''}>
                {other.name}
              </option>
            ))}
          </Select>
          {stage.advance_on_approval && (
            <p className="text-muted-foreground text-xs">
              The case moves by itself, recorded as an automatic move with the approval as its
              reason. It still has to pass the checks a person would: if this stage has tasks left to
              do, or the case is with a reviewer, the approval is recorded and the case stays put — and
              the timeline says why.
            </p>
          )}
        </div>

        <div className="space-y-2">
          <p className="text-foreground text-sm font-medium">
            Tasks that must be done before the case leaves this stage
          </p>
          {stage.required_tasks.map((task, t) => (
            <div key={t} className="flex items-end gap-2">
              <div className="min-w-0 flex-1 sm:max-w-md">
                <Input
                  aria-label={`Task ${t + 1}`}
                  value={task.name}
                  disabled={!live}
                  onChange={(e) => {
                    const next = [...stage.required_tasks]
                    next[t] = { ...task, name: e.target.value }
                    update({ required_tasks: next })
                  }}
                />
              </div>
              {live && (
                <Button
                  variant="ghost"
                  aria-label={`Remove task ${t + 1}`}
                  onClick={() => update({ required_tasks: stage.required_tasks.filter((_, i) => i !== t) })}
                >
                  <Trash2 size={16} aria-hidden />
                </Button>
              )}
            </div>
          ))}
          {live && (
            <Button
              variant="ghost"
              onClick={() => update({ required_tasks: [...stage.required_tasks, { name: '', role: null }] })}
            >
              <Plus size={16} aria-hidden /> Add a task
            </Button>
          )}
        </div>

        <div className="space-y-2">
          <p className="text-foreground text-sm font-medium">What happens when a case enters this stage</p>
          {stage.actions.map((action, a) => (
            <div key={a} className="flex flex-wrap items-end gap-2">
              <span className="text-foreground pb-2 text-sm">{ACTION_LABELS[action.kind] ?? action.kind}</span>
              {action.kind === 'notify' && (
                <div className="min-w-0 flex-1 sm:max-w-md">
                  <Input
                    label="Message template"
                    value={action.config.template ?? ''}
                    disabled={!live}
                    onChange={(e) => {
                      const next = [...stage.actions]
                      next[a] = { ...action, config: { template: e.target.value } }
                      update({ actions: next })
                    }}
                  />
                </div>
              )}
              {action.kind === 'assign_reviewer' && (
                <Select
                  label="Reviewed by"
                  value={action.config.role ?? ''}
                  disabled={!live}
                  required
                  aria-required="true"
                  error={fieldError(`actions.${action.kind}.role`)}
                  onChange={(e) => {
                    const next = [...stage.actions]
                    next[a] = { ...action, config: { role: e.target.value } }
                    update({ actions: next })
                  }}
                >
                  <option value="" disabled hidden>
                    choose a role
                  </option>
                  {moverRoles.map((role) => (
                    <option key={role.code} value={role.code}>
                      {role.name}
                    </option>
                  ))}
                </Select>
              )}
              {live && (
                <Button
                  variant="ghost"
                  aria-label={`Remove ${ACTION_LABELS[action.kind] ?? action.kind}`}
                  onClick={() => update({ actions: stage.actions.filter((_, i) => i !== a) })}
                >
                  <Trash2 size={16} aria-hidden />
                </Button>
              )}
            </div>
          ))}
          {live && (
            <div className="flex flex-wrap gap-2">
              {actionKinds.map(({ kind }) => (
                <Button
                  key={kind}
                  variant="ghost"
                  onClick={() =>
                    update({ actions: [...stage.actions, { kind: kind as ActionKind, config: {} }] })
                  }
                >
                  <Plus size={16} aria-hidden /> {ACTION_LABELS[kind] ?? kind}
                </Button>
              ))}
            </div>
          )}
        </div>
      </CardBody>
    </Card>
  )
}
